import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';

const CallbackPage = () => {
  const { handleRedirectCallback, error } = useAuth0();
  const { subdomain, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [callbackProcessed, setCallbackProcessed] = useState(false);

  // Check if we're on the problematic URL and fix it immediately
  useEffect(() => {
    const currentPath = window.location.pathname;
    if (currentPath.includes('[object') || currentPath.includes('object%20Object')) {
      console.warn('Detected corrupted callback URL, redirecting to clean callback');
      window.location.replace('/callback');
      return;
    }
  }, []);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Check if we have the required Auth0 query parameters
        const urlParams = new URLSearchParams(window.location.search);
        const hasCode = urlParams.has('code');
        const hasState = urlParams.has('state');
        
        console.log('Callback params:', { hasCode, hasState });
        
        if (hasCode && hasState) {
          console.log('Calling handleRedirectCallback...');
          const result = await handleRedirectCallback();
          console.log('handleRedirectCallback result:', result);
          setCallbackProcessed(true);
        } else {
          console.log('No code/state params, skipping callback handling');
          // If we don't have the required parameters, this might be a direct navigation
          setCallbackProcessed(true);
        }
      } catch (err) {
        console.error('Error handling callback:', err);
        setCallbackProcessed(true);
        // Don't let callback errors prevent the redirect - user might already be authenticated
      }
    };

    if (!callbackProcessed) {
      handleCallback();
    }
  }, [handleRedirectCallback, callbackProcessed]);

  // Clean up any problematic session storage on component mount
  useEffect(() => {
    const storedRedirect = sessionStorage.getItem('postLoginRedirect');
    if (storedRedirect && (storedRedirect === '[object Object]' || storedRedirect.includes('object'))) {
      console.warn('Removing corrupted postLoginRedirect:', storedRedirect);
      sessionStorage.removeItem('postLoginRedirect');
    }
  }, []);

  // Debug logging for callback state
  useEffect(() => {
    console.log('CallbackPage state:', { isAuthenticated, subdomain, callbackProcessed });
  }, [isAuthenticated, subdomain, callbackProcessed]);

  // Redirect to dashboard once authenticated (subdomain-based tenancy)
  useEffect(() => {
    if (isAuthenticated && subdomain && callbackProcessed) {
      console.log('All conditions met, redirecting to dashboard');
      // Check if there's a stored post-login redirect path
      const storedRedirect = sessionStorage.getItem('postLoginRedirect');
      sessionStorage.removeItem('postLoginRedirect'); // Clean up
      
      // Check if there's a returnTo parameter in the URL (fallback)
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get('returnTo');
      
      // Validate stored redirect is a proper string and not an object
      const isValidRedirect = storedRedirect && 
        typeof storedRedirect === 'string' && 
        storedRedirect !== '[object Object]' &&
        storedRedirect.startsWith('/');
      
      // Validate returnTo is a proper string and not an object  
      const isValidReturnTo = returnTo && 
        typeof returnTo === 'string' && 
        returnTo !== '[object Object]' &&
        returnTo.startsWith('/');
      
      if (isValidRedirect) {
        navigate(storedRedirect, { replace: true });
      } else if (isValidReturnTo) {
        navigate(returnTo, { replace: true });
      } else {
        // Redirect to dashboard - tenant is determined by subdomain on backend
        navigate('/dashboard', { replace: true });
      }
    }
  }, [isAuthenticated, subdomain, navigate, callbackProcessed]);

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column' 
      }}>
        <Typography variant="h6" color="error" gutterBottom>
          Authentication Error
        </Typography>
        <Typography variant="body2">
          {error.message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      height: '100vh',
      flexDirection: 'column' 
    }}>
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        {!callbackProcessed ? 'Completing authentication...' : 'Setting up your organization...'}
      </Typography>
    </Box>
  );
};

export default CallbackPage;
